import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NotificationCenter = ({ notifications, onMarkAsRead, onMarkAllAsRead, onNotificationAction }) => {
  const [filter, setFilter] = useState('all');

  const filterOptions = [
    { value: 'all', label: 'All', icon: 'Bell' },
    { value: 'milestone', label: 'Milestones', icon: 'Target' },
    { value: 'team', label: 'Teams', icon: 'Users' },
    { value: 'meeting', label: 'Meetings', icon: 'Calendar' },
    { value: 'system', label: 'System', icon: 'Settings' }
  ];

  const filteredNotifications = notifications?.filter(notification => 
    filter === 'all' || notification?.type === filter
  );

  const unreadCount = notifications?.filter(n => !n?.isRead)?.length;

  const getNotificationIcon = (type, priority) => {
    const iconMap = {
      milestone: 'Target',
      team: 'Users',
      meeting: 'Calendar',
      system: 'Settings',
      message: 'MessageSquare',
      deadline: 'Clock'
    };
    
    const iconName = iconMap?.[type] || 'Bell';
    const colorClass = priority === 'high' ? 'text-error' : 
                      priority === 'medium' ? 'text-warning' : 'text-muted-foreground';
    
    return <Icon name={iconName} size={20} className={colorClass} />;
  };

  const formatNotificationTime = (timestamp) => {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - notificationTime) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return notificationTime?.toLocaleDateString();
  };

  const getPriorityBadge = (priority) => {
    const badgeClasses = {
      high: 'bg-error text-error-foreground',
      medium: 'bg-warning text-warning-foreground',
      low: 'bg-muted text-muted-foreground'
    };
    
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${badgeClasses?.[priority]}`}>
        {priority?.toUpperCase()}
      </span>
    );
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="p-4 border-b border-border bg-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="Bell" size={24} className="text-primary" />
            <h2 className="text-lg font-semibold text-foreground">Notifications</h2>
            {unreadCount > 0 && (
              <span className="bg-error text-error-foreground text-xs px-2 py-1 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onMarkAllAsRead}
              iconName="CheckCheck"
              iconPosition="left"
              iconSize={14}
            >
              Mark all read
            </Button>
          )}
        </div>

        {/* Filter Tabs */}
        <div className="flex space-x-1 bg-muted rounded-lg p-1">
          {filterOptions?.map((option) => (
            <Button
              key={option?.value}
              variant={filter === option?.value ? "default" : "ghost"}
              size="sm"
              onClick={() => setFilter(option?.value)}
              iconName={option?.icon}
              iconPosition="left"
              iconSize={14}
              className="flex-1 transition-micro"
            >
              {option?.label}
            </Button>
          ))}
        </div>
      </div>
      {/* Notifications List */}
      <div className="flex-1 overflow-y-auto">
        {filteredNotifications?.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center p-6">
            <Icon name="BellOff" size={48} className="text-muted-foreground mb-4" />
            <h3 className="font-medium text-foreground mb-2">No notifications</h3>
            <p className="text-sm text-muted-foreground">
              {filter === 'all' ? 'You\'re all caught up!' : `No ${filter} notifications found`}
            </p>
          </div>
        ) : (
          <div className="space-y-1 p-2">
            {filteredNotifications?.map((notification) => (
              <div
                key={notification?.id}
                className={`p-4 rounded-lg border transition-micro hover:bg-muted cursor-pointer ${
                  notification?.isRead 
                    ? 'bg-background border-border' :'bg-primary/5 border-primary/20'
                }`}
                onClick={() => onMarkAsRead(notification?.id)}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1">
                    {getNotificationIcon(notification?.type, notification?.priority)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className={`font-medium truncate ${
                        notification?.isRead ? 'text-muted-foreground' : 'text-foreground'
                      }`}>
                        {notification?.title}
                      </h4>
                      <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                        {notification?.priority !== 'low' && getPriorityBadge(notification?.priority)}
                        <span className="text-xs text-muted-foreground">
                          {formatNotificationTime(notification?.timestamp)}
                        </span>
                      </div>
                    </div>
                    
                    <p className={`text-sm mb-2 ${
                      notification?.isRead ? 'text-muted-foreground' : 'text-foreground'
                    }`}>
                      {notification?.message}
                    </p>
                    
                    {notification?.actionRequired && (
                      <div className="flex items-center space-x-2 mt-3">
                        <Button
                          variant="default"
                          size="sm"
                          onClick={(e) => {
                            e?.stopPropagation();
                            onNotificationAction(notification?.id, 'primary');
                          }}
                        >
                          {notification?.primaryAction || 'View'}
                        </Button>
                        {notification?.secondaryAction && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e?.stopPropagation();
                              onNotificationAction(notification?.id, 'secondary');
                            }}
                          >
                            {notification?.secondaryAction}
                          </Button>
                        )}
                      </div>
                    )}
                    
                    {notification?.metadata && (
                      <div className="flex items-center space-x-4 mt-2 text-xs text-muted-foreground">
                        {notification?.metadata?.sender && (
                          <span>From: {notification?.metadata?.sender}</span>
                        )}
                        {notification?.metadata?.project && (
                          <span>Project: {notification?.metadata?.project}</span>
                        )}
                        {notification?.metadata?.deadline && (
                          <span className="flex items-center space-x-1">
                            <Icon name="Clock" size={12} />
                            <span>Due: {new Date(notification.metadata.deadline)?.toLocaleDateString()}</span>
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {!notification?.isRead && (
                    <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-2" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationCenter;